// MSAL configuration for Entra External ID (CIAM).
//
// Values come from Vite env vars (VITE_*), exposed at build time. None are secrets —
// they are public tenant/client identifiers — but keeping them in env lets the same
// build target dev vs prod tenants.
//
// CIAM specifics (learned the hard way, see DevOps notes 2026-07-22):
//   - The authority host is <subdomain>.ciamlogin.com, NOT login.microsoftonline.com.
//   - The authority uses the TENANT ID in the path.
//   - There is NO B2C_1_ user-flow segment in the authority (that was legacy B2C).
//     The tenant routes to whatever user flow is bound to this app registration.

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID
  || 'd0fa7da3-a3e6-44fc-9962-8a1ea42650e1';
const SUBDOMAIN = import.meta.env.VITE_ENTRA_SUBDOMAIN || 'eagleridgeclub';
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID
  || 'da6f611d-3805-4703-838f-ddb94fe8a2ad'; // SPA app: eagle-ridge-web

// The API scope the SPA requests so its access token is accepted by the Functions API.
// api://<api-client-id>/access_as_user
const API_SCOPE = import.meta.env.VITE_API_SCOPE
  || 'api://7a414558-4c55-4bd9-bdce-79c4fba4b678/access_as_user';

export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://${SUBDOMAIN}.ciamlogin.com/${TENANT_ID}`,
    knownAuthorities: [`${SUBDOMAIN}.ciamlogin.com`],
    // Where Entra returns after login. Must be registered as an SPA redirect URI.
    // In dev this is http://localhost:5173; in prod the SWA URL. window.location.origin
    // resolves both without a rebuild.
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage is safer than localStorage (cleared on tab close, smaller
    // XSS token-theft window). Fine for a member portal.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// Scopes requested at login. openid/profile for identity; the API scope so we get
// an access token the backend will accept.
export const loginRequest = {
  scopes: ['openid', 'profile', API_SCOPE],
};

// Scopes for silent token acquisition before calling the API.
export const apiRequest = {
  scopes: [API_SCOPE],
};

// Base URL for the Functions API.
//   - dev: empty string -> relative /api, Vite proxies to http://localhost:7071
//   - prod: the Function App origin (SWA Free SKU can't link a backend, so the SPA
//     calls the Function App directly; CORS on the Function App allows the SWA origin)
// VITE_API_BASE_URL overrides both. On the SWA host we default to the Function App.
const DEFAULT_PROD_API_BASE = 'https://func-eagleridge-dev-3lszv3u2efvce.azurewebsites.net';
function resolveApiBase() {
  if (import.meta.env.VITE_API_BASE_URL != null) return import.meta.env.VITE_API_BASE_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
    // Any deployed host (SWA default domain OR custom domain like eagleridgeclub.com)
    // calls the Function App directly. Only local dev uses the same-origin Vite proxy.
    if (!isLocal) return DEFAULT_PROD_API_BASE;
  }
  return ''; // dev / same-origin
}
export const apiBase = resolveApiBase();
